import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { getUserRole } from '../utils/jwtUtils';

const AuthContext = createContext({});

// Configure axios defaults - use empty baseURL since nginx handles routing
// In production, nginx proxies /api/* to the backend
axios.defaults.baseURL = '';

// Add request interceptor to include token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
// Only redirect if not already on login page to prevent loops
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      // Only redirect if not already on root/login page
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        await validateToken();
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const validateToken = async () => {
    try {
      // Add cache-busting timestamp to prevent browser caching
      const response = await axios.get('/api/me', {
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      // Clear invalid auth data without redirecting (prevent loops)
      setUser(null);
      setError(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  };

  const login = async (username, password) => {
    setError(null);
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post('/api/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      setToken(access_token);

      // Get user info for all roles with cache-busting
      const userResponse = await axios.get('/api/me', {
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setUser(userResponse.data);
      localStorage.setItem('user', JSON.stringify(userResponse.data));

      return { success: true };
    } catch (error) {
      let message = error.response?.data?.detail || 'Login failed';

      // Handle case where detail might be a JSON string (from API gateway wrapping)
      if (typeof message === 'string' && message.startsWith('{')) {
        try {
          const parsed = JSON.parse(message);
          message = parsed.detail || message;
        } catch (e) {
          // Keep original message if parsing fails
        }
      }

      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    // Clear user state immediately
    setUser(null);
    setToken(null);
    setError(null);

    // Clear all auth-related localStorage items
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');

    // Force full page reload to clear all state
    // Only redirect if not already on login page
    if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
      window.location.href = '/';
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isRenter: user?.role === 'renter',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
